import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

/** ISO yyyy-mm-dd → dd/mm/yyyy (hiển thị VN). */
export function isoSangVn(iso) {
  const s = String(iso ?? "")
    .trim()
    .slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
}

/** Chuỗi người dùng (dd/mm/yyyy, dd-mm-yyyy, ddmmyyyy…) → ISO hoặc "". */
export function vnSangIso(raw) {
  const digits = String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, 8);
  if (digits.length !== 8) return "";
  const d = Number(digits.slice(0, 2));
  const m = Number(digits.slice(2, 4));
  const y = Number(digits.slice(4, 8));
  if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return "";
  const ngay = new Date(y, m - 1, d);
  if (ngay.getFullYear() !== y || ngay.getMonth() !== m - 1 || ngay.getDate() !== d) return "";
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(
    2,
    "0"
  )}`;
}

/** Mask nhập: chỉ số → dd/mm/yyyy dần. */
export function maskNgayVn(raw) {
  const digits = String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Ô ngày: gõ dd/mm/yyyy (mobile-friendly) hoặc bấm lịch chọn native.
 * value/onChange luôn ISO yyyy-mm-dd (Firestore).
 */
function NhapNgay({ label, value, onChange, required, disabled, fullWidth, helperText }) {
  const pickerRef = useRef(null);
  const [hienThi, setHienThi] = useState(() => isoSangVn(value));

  useEffect(() => {
    setHienThi(isoSangVn(value));
  }, [value]);

  function guiIso(iso) {
    onChange(iso);
  }

  function khiGo(raw) {
    const masked = maskNgayVn(raw);
    setHienThi(masked);
    const digits = masked.replace(/\D/g, "");
    if (digits.length === 0) {
      guiIso("");
      return;
    }
    if (digits.length === 8) {
      const iso = vnSangIso(masked);
      guiIso(iso || "");
      return;
    }
    // Đang gõ dở — xóa ISO cũ để không lưu nhầm ngày trước đó.
    if (value) guiIso("");
  }

  function khiBlur() {
    const iso = vnSangIso(hienThi);
    if (iso) {
      setHienThi(isoSangVn(iso));
      if (iso !== (value ?? "")) guiIso(iso);
      return;
    }
    // Rỗng hoặc không hợp lệ → đồng bộ parent + UI về trống.
    setHienThi("");
    if (value) guiIso("");
  }

  function moLich() {
    const el = pickerRef.current;
    if (!el || disabled) return;
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        /* fallback click */
      }
    }
    el.click();
  }

  return (
    <MDBox position="relative" width={fullWidth ? "100%" : undefined}>
      <MDInput
        label={label}
        value={hienThi}
        onChange={(e) => khiGo(e.target.value)}
        onBlur={khiBlur}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        placeholder="dd/mm/yyyy"
        helperText={helperText}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          inputMode: "numeric",
          autoComplete: "bday",
          "aria-label": label,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                size="small"
                aria-label="Chọn ngày trên lịch"
                onClick={moLich}
                disabled={disabled}
                tabIndex={-1}
              >
                <CalendarMonthIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {/* Picker ẩn — mở bằng nút lịch; value vẫn ISO. */}
      <input
        ref={pickerRef}
        type="date"
        value={value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ""}
        onChange={(e) => {
          const iso = e.target.value;
          guiIso(iso);
          setHienThi(isoSangVn(iso));
        }}
        tabIndex={-1}
        aria-hidden
        style={{
          position: "absolute",
          opacity: 0,
          width: 0,
          height: 0,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          bottom: 0,
          right: 40,
          border: 0,
          padding: 0,
          margin: 0,
        }}
      />
    </MDBox>
  );
}

NhapNgay.defaultProps = {
  value: "",
  required: false,
  disabled: false,
  fullWidth: true,
  helperText: "Gõ dd/mm/yyyy hoặc bấm lịch",
};

NhapNgay.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.string,
};

export default NhapNgay;
